/**
 * Command palette entries from the action registry (DESIGN.md §9.1, §18). Pages register the
 * actions that apply to their current selection with `usePaletteActions`; `AppShell` lists them
 * in the palette next to its static actions and the pages.
 */
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import type { PaletteAction } from '../composites/command-palette';
import { useUiStrings } from '../strings';
import { labelText, type ResolvedAction } from './registry';
import { useRunAction } from './runner';
import { shortcutText } from './shortcut-label';

type Register = (scope: string, actions: readonly PaletteAction[] | undefined) => void;

const RegisterContext = createContext<Register | null>(null);
const ListContext = createContext<readonly PaletteAction[]>([]);

/** Collects page actions for the palette (read them with `useRegisteredPaletteActions`). */
export function PaletteActionsProvider({ children }: { children: ReactNode }) {
  const [scopes, setScopes] = useState<ReadonlyMap<string, readonly PaletteAction[]>>(new Map());
  const register = useCallback<Register>((scope, actions) => {
    setScopes((current) => {
      const next = new Map(current);
      if (actions) next.set(scope, actions);
      else next.delete(scope);
      return next;
    });
  }, []);
  const merged = useMemo(() => [...scopes.values()].flat(), [scopes]);
  return (
    <RegisterContext.Provider value={register}>
      <ListContext.Provider value={merged}>{children}</ListContext.Provider>
    </RegisterContext.Provider>
  );
}

/** Actions the mounted pages currently offer to the palette. */
export function useRegisteredPaletteActions(): readonly PaletteAction[] {
  return useContext(ListContext);
}

/**
 * Offers `actions` (resolved with source `palette`) in the command palette while the component is
 * mounted. Planned and disabled actions are left out. Selecting one runs the current version.
 */
export function usePaletteActions<T>(actions: readonly ResolvedAction<T>[]): void {
  const register = useContext(RegisterContext);
  const strings = useUiStrings();
  const run = useRunAction();
  const scope = useId();
  const latest = useRef(actions);
  useEffect(() => {
    latest.current = actions;
  });

  const entries = actions.filter((entry) => entry.enabled);
  // Re-register only when what the palette shows changes, not on every render.
  const signature = entries.map((entry) => `${entry.action.id}=${labelText(strings.translate, entry.label)}`).join('|');

  useEffect(() => {
    if (!register) return;
    const shown = latest.current.filter((entry) => entry.enabled);
    register(
      scope,
      shown.map((entry) => ({
        id: entry.action.id,
        label: labelText(strings.translate, entry.label),
        icon: entry.action.icon,
        shortcut: shortcutText(entry.action.shortcut, strings.keys),
        onSelect: () => {
          const current = latest.current.find((candidate) => candidate.action.id === entry.action.id);
          if (current) run(current);
        },
      })),
    );
    return () => register(scope, undefined);
  }, [register, scope, signature, strings, run]);
}
